import React, {useState, useEffect} from 'react'
import {Modal, Button, Table, Form, InputGroup, Spinner, Alert} from 'react-bootstrap'
import {useLazyQuery, useMutation} from '@apollo/client'
import {GET_ALL_CONTACTS_API} from '../../../gql/queries/contactsApiQueries'
import {ADD_TO_RECIPIENTS} from '../../../gql/mutations/recipients'
import {IResContactsAPI} from '../../../types/contactsApi'
import {PhoneProviderActions} from '../../../components/telephony/PhoneProviderActions'
import {toast} from 'react-toastify'
import {useIntl, FormattedMessage} from 'react-intl'
import {RECIPIENT_MESSAGES as M, EM_DASH, fullName} from '../recipientMessages'
import './AddRecipientsModal.css'

interface AddRecipientsModalProps {
  show: boolean
  onHide: () => void
  onSuccess?: () => void
}

const PAGE_SIZE = 20

export const AddRecipientsModal: React.FC<AddRecipientsModalProps> = ({
  show,
  onHide,
  onSuccess,
}) => {
  const intl = useIntl()
  const [searchTerm, setSearchTerm] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const [currentPage, setCurrentPage] = useState(1)

  const [getContacts, {data, loading}] = useLazyQuery<IResContactsAPI>(GET_ALL_CONTACTS_API, {
    fetchPolicy: 'network-only',
  })

  const [addToRecipients, {loading: adding}] = useMutation(ADD_TO_RECIPIENTS)

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm)
    }, 300)
    return () => clearTimeout(timer)
  }, [searchTerm])

  // Fetch NON-recipient contacts
  useEffect(() => {
    if (show) {
      const offset = (currentPage - 1) * PAGE_SIZE
      getContacts({
        variables: {
          name: debouncedSearch || undefined,
          first: PAGE_SIZE,
          offset,
          isRecipient: false, // Only NON-recipients
        },
      })
    }
  }, [show, debouncedSearch, currentPage, getContacts])

  const contacts = data?.allContacts?.edges || []
  const totalCount = data?.allContacts?.totalCount || 0
  const totalPages = Math.ceil(totalCount / PAGE_SIZE)

  const toggleSelect = (contactId: string) => {
    setSelectedIds(prev =>
      prev.includes(contactId)
        ? prev.filter(id => id !== contactId)
        : [...prev, contactId]
    )
  }

  const toggleSelectAll = () => {
    if (selectedIds.length === contacts.length && contacts.length > 0) {
      setSelectedIds([])
    } else {
      setSelectedIds(contacts.map(edge => edge.node.id))
    }
  }

  const handleAdd = async () => {
    if (selectedIds.length === 0) {
      toast.error(intl.formatMessage(M.toastSelectContact))
      return
    }

    try {
      const result = await addToRecipients({
        variables: {contactIds: selectedIds},
      })
      toast.success(
        intl.formatMessage(M.toastAdded, {count: result.data.addToRecipients.addedCount})
      )
      handleClose()
      onSuccess?.()
    } catch (error: any) {
      toast.error(error.message || intl.formatMessage(M.toastErrorAdd))
    }
  }

  const handleClose = () => {
    setSelectedIds([])
    setSearchTerm('')
    setCurrentPage(1)
    onHide()
  }

  const isAllSelected = contacts.length > 0 && selectedIds.length === contacts.length

  return (
    <Modal show={show} onHide={handleClose} size='xl' centered>
      <Modal.Header closeButton>
        <Modal.Title><FormattedMessage {...M.addRecipients} /></Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className='mb-4'>
          <Alert variant='info'>
            <i className='bi bi-info-circle me-2'></i>
            <FormattedMessage {...M.addHint} />
          </Alert>

          <InputGroup>
            <InputGroup.Text>
              <i className='bi bi-search'></i>
            </InputGroup.Text>
            <Form.Control
              type='text'
              placeholder={intl.formatMessage(M.searchPlaceholder)}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </InputGroup>
        </div>

        {selectedIds.length > 0 && (
          <Alert variant='primary' className='mb-3'>
            <FormattedMessage
              {...M.contactsSelected}
              values={{
                count: selectedIds.length,
                b: (chunks: React.ReactNode) => <strong>{chunks}</strong>,
              }}
            />
          </Alert>
        )}

        <div className='table-container' style={{maxHeight: '400px', overflowY: 'auto'}}>
          {loading ? (
            <div className='text-center py-5'>
              <Spinner animation='border' />
            </div>
          ) : contacts.length === 0 ? (
            <Alert variant='warning'>
              <FormattedMessage {...M.addEmpty} />
            </Alert>
          ) : (
            <Table hover size='sm'>
              <thead style={{position: 'sticky', top: 0, backgroundColor: 'white', zIndex: 1}}>
                <tr>
                  <th style={{width: '50px'}}>
                    <Form.Check
                      type='checkbox'
                      checked={isAllSelected}
                      onChange={toggleSelectAll}
                    />
                  </th>
                  <th><FormattedMessage {...M.name} /></th>
                  <th><FormattedMessage {...M.email} /></th>
                  <th><FormattedMessage {...M.phone} /></th>
                </tr>
              </thead>
              <tbody>
                {contacts.map(({node: contact}) => (
                  <tr key={contact.id}>
                    <td>
                      <Form.Check
                        type='checkbox'
                        checked={selectedIds.includes(contact.id)}
                        onChange={() => toggleSelect(contact.id)}
                      />
                    </td>
                    <td>
                      <strong>{contact.fullName || fullName(contact.firstName, contact.lastName)}</strong>
                    </td>
                    <td>{contact.email || <span className='text-muted'>{EM_DASH}</span>}</td>
                    <td>
                      <PhoneProviderActions phone={contact.phone} emptyText={EM_DASH} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </div>

        {totalPages > 1 && (
          <div className='d-flex justify-content-between align-items-center mt-3'>
            <div className='text-muted small'>
              <FormattedMessage
                {...M.addShowing}
                values={{shown: contacts.length, total: totalCount}}
              />
            </div>
            <div className='d-flex gap-2'>
              <Button
                size='sm'
                variant='outline-secondary'
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(prev => prev - 1)}
              >
                <FormattedMessage {...M.previous} />
              </Button>
              <span className='align-self-center small'>
                <FormattedMessage {...M.pageOf} values={{page: currentPage, total: totalPages}} />
              </span>
              <Button
                size='sm'
                variant='outline-secondary'
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage(prev => prev + 1)}
              >
                <FormattedMessage {...M.next} />
              </Button>
            </div>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant='secondary' onClick={handleClose}>
          <FormattedMessage {...M.cancel} />
        </Button>
        <Button
          variant='primary'
          onClick={handleAdd}
          disabled={adding || selectedIds.length === 0}
        >
          {adding ? (
            <>
              <span className='spinner-border spinner-border-sm me-2' />
              <FormattedMessage {...M.adding} />
            </>
          ) : (
            <FormattedMessage {...M.addAction} values={{count: selectedIds.length}} />
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
